// Reusable filter dropdown component with multi-select support
// CREATED: For admin pages filtering feature
// Supports categories, badges for active filters, and reset functionality
// CHANGE: Added intelligent positioning to prevent dropdown from being cut off at screen edges
// Updated: Increased touch target sizes to 48px min height (WCAG 2.1 Level AA compliant)

import { useState, useRef, useEffect } from 'react';
import { Filter, X, ChevronDown } from 'lucide-react';

export interface FilterOption {
  value: string;
  label: string;
  count?: number;
}

interface FilterDropdownProps {
  label: string;
  options: FilterOption[];
  selectedValues: string[];
  onChange: (values: string[]) => void;
  className?: string;
  placeholder?: string;
}

export function FilterDropdown({
  label,
  options,
  selectedValues,
  onChange,
  className = '',
  placeholder = 'Sélectionner...'
}: FilterDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [dropdownPosition, setDropdownPosition] = useState<{
    vertical: 'bottom' | 'top';
    horizontal: 'left' | 'right';
  }>({ vertical: 'bottom', horizontal: 'left' });

  // Calculate optimal dropdown position when opening
  useEffect(() => {
    if (isOpen && dropdownRef.current) {
      const rect = dropdownRef.current.getBoundingClientRect();
      const dropdownHeight = 400; // Approximate max height (64 * 4 + headers)
      const dropdownWidth = 256; // w-64 = 16rem = 256px

      // Check vertical space
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      const vertical = spaceBelow >= dropdownHeight || spaceBelow > spaceAbove ? 'bottom' : 'top';

      // Check horizontal space
      const spaceRight = window.innerWidth - rect.left;
      const horizontal = spaceRight >= dropdownWidth ? 'left' : 'right';

      setDropdownPosition({ vertical, horizontal });
    }
  }, [isOpen]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  const handleToggleOption = (value: string) => {
    if (selectedValues.includes(value)) {
      onChange(selectedValues.filter(v => v !== value));
    } else {
      onChange([...selectedValues, value]);
    }
  };

  const handleClearAll = () => {
    onChange([]);
  };

  const getSelectedLabels = () => {
    if (selectedValues.length === 0) return placeholder;
    if (selectedValues.length === 1) {
      const option = options.find(o => o.value === selectedValues[0]);
      return option?.label || selectedValues[0];
    }
    return `${selectedValues.length} sélectionné${selectedValues.length > 1 ? 's' : ''}`;
  };

  return (
    <div ref={dropdownRef} className={`relative ${className}`}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center gap-2 px-4 py-2.5 border rounded-lg transition-all text-sm font-medium ${
          selectedValues.length > 0
            ? 'bg-primary-50 border-primary-300 text-primary-700 hover:bg-primary-100'
            : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
        }`}
      >
        <Filter size={16} />
        <span>{label}</span>
        {selectedValues.length > 0 && (
          <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-primary-600 text-white text-xs font-semibold">
            {selectedValues.length}
          </span>
        )}
        <ChevronDown size={16} className={`transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className={`absolute w-64 bg-white border border-gray-200 rounded-lg shadow-lg z-50 ${
          dropdownPosition.vertical === 'bottom' ? 'top-full mt-2' : 'bottom-full mb-2'
        } ${
          dropdownPosition.horizontal === 'left' ? 'left-0' : 'right-0'
        }`}>
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
            <span className="text-sm font-semibold text-gray-700">{label}</span>
            {selectedValues.length > 0 && (
              <button
                onClick={handleClearAll}
                className="text-xs text-primary-600 hover:text-primary-700 font-medium"
              >
                Tout effacer
              </button>
            )}
          </div>

          {/* Options */}
          <div className="max-h-64 overflow-y-auto py-2">
            {options.length === 0 ? (
              <div className="px-4 py-3 text-sm text-gray-500 text-center">
                Aucune option disponible
              </div>
            ) : (
              options.map((option) => (
                <label
                  key={option.value}
                  className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 cursor-pointer transition-colors"
                  style={{ minHeight: '48px' }}
                >
                  <input
                    type="checkbox"
                    checked={selectedValues.includes(option.value)}
                    onChange={() => handleToggleOption(option.value)}
                    className="w-5 h-5 text-primary-600 border-gray-300 rounded focus:ring-primary-500 flex-shrink-0"
                  />
                  <span className="flex-1 text-sm text-gray-700">{option.label}</span>
                  {option.count !== undefined && (
                    <span className="text-xs text-gray-500 font-medium">
                      {option.count}
                    </span>
                  )}
                </label>
              ))
            )}
          </div>

          {/* Footer with selected count */}
          {selectedValues.length > 0 && (
            <div className="px-4 py-2.5 border-t border-gray-100 bg-gray-50">
              <span className="text-xs text-gray-600">
                {getSelectedLabels()}
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
